import prettyMilliseconds from "pretty-ms";
import { ReactNode } from "react";
import {
    Comparison,
    FieldTypedValueBase,
    FormFieldType,
    FormFieldVisibility,
    MetricsDashboardData, MetricsDashboardDataRow, MetricsDashboardGraph,
    MetricsDashboardGraphDataSource,
    MetricsDashboardGraphDimension,
    MetricsDashboardGraphDimensionFormat,
    MetricsDashboardGraphFilter,
    MetricsDashboardGraphMetric,
    MetricsDashboardGraphType,
    MetricsDashboardGraphTypeAggregation, MetricsDashboardGraphTypeDisplayType,
} from "../../../__generated__/api";
import { ReplacementGroup } from "../../../components/replacement-group";
import {
    ASSIGNEE_FIELDS, CREATOR_FIELDS, REQUEST_FIELD_INPUTS, REQUESTOR_FIELDS, SOURCE_FIELDS,
} from "../../../components/workflow/WorkflowFields";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";
import {
    toInputDate, toInputHourMinute,
    toUTCInputHourMinute, WEEKDAYS,
} from "../../../date";

export interface GraphProps {
    graph: MetricsDashboardGraph;
    data: MetricsDashboardData;
    columnIndexMap: Record<string, number>;
    labelMap: Record<string, string>;
    testId?: string;
    createMenu?: (value: MetricsDashboardGraph) => ReactNode;
    isAdmin?: boolean
}

export const REQUEST_FIELDS: ReplacementGroup = {
    id: "urn:fq:form-field-group:request",
    label: "Request Fields",
    description: "Request information",
    input: [...REQUEST_FIELD_INPUTS, {
        id: "urn:fq:form-field:id",
        name: "Ticket ID",
        type: FormFieldType.Text,
        visibility: FormFieldVisibility.Private,
        isSystemField: true,
    },
    ],

};

export const FIELDS = [
    REQUEST_FIELDS,
    SOURCE_FIELDS,
    REQUESTOR_FIELDS,
    CREATOR_FIELDS,
    ASSIGNEE_FIELDS,
];

function getCSSProperty(name: string) {
    const style = getComputedStyle(document.body);
    return style.getPropertyValue(name);
}

export const CHART_COLORS = {
    grid: () => getCSSProperty("--fq-border-primary-color"),
    text: () => getCSSProperty("--fq-text-body-secondary-color"),
    textDim: () => getCSSProperty("--fq-text-body-dim-color"),
};

export interface GraphSettingsProperties {
    graph: MetricsDashboardGraph;
    isAdmin: boolean;
    onChange: (value: MetricsDashboardGraph) => void;
}

export function formatValueWithDisplay(value: number | undefined, display: MetricsDashboardGraphTypeDisplayType) {
    let textValue;
    if (value == null) {
        textValue = "-";
    } else if (display === MetricsDashboardGraphTypeDisplayType.Float) {
        textValue = value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
        });
    } else if (display === MetricsDashboardGraphTypeDisplayType.Currency) {
        textValue = `$ ${value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
        })}`;
    } else if (display === MetricsDashboardGraphTypeDisplayType.Percentage) {
        textValue = `${(value * 100).toLocaleString(undefined, {
            minimumFractionDigits: 2,
        })}%`;
    } else if (display === MetricsDashboardGraphTypeDisplayType.Duration) {
        try {
            textValue = prettyMilliseconds(value || 0, { hideYearAndDays: true, hideSeconds: true });
        } catch (err) {
            textValue = "-";
            LoggingHandler.instance.error({ err }, "Error parsing duration in a report");
        }
    } else {
        textValue = Math.round(value);
    }
    return textValue;
}

export function createDisplayFormatter(display: MetricsDashboardGraphTypeDisplayType) {
    return (value: number) => formatValueWithDisplay(value, display);
}

// eslint-disable-next-line max-len
export function getMetricValue(values: string[], aggregation = MetricsDashboardGraphTypeAggregation.Average): number | undefined {
    if (aggregation === MetricsDashboardGraphTypeAggregation.Average) {
        return values.isNotEmpty ? values.average(row => parseFloat(row)) : undefined;
    }
    if (aggregation === MetricsDashboardGraphTypeAggregation.Count) {
        return values.length;
    }
    if (aggregation === MetricsDashboardGraphTypeAggregation.CountDistinct) {
        return values.dedupe().length;
    }
    if (aggregation === MetricsDashboardGraphTypeAggregation.Max) {
        return values.maxValue(row => parseFloat(row));
    }
    if (aggregation === MetricsDashboardGraphTypeAggregation.Min) {
        return values.minValue(row => parseFloat(row));
    }
    if (aggregation === MetricsDashboardGraphTypeAggregation.Median) {
        return values.isNotEmpty ? values.median(row => parseFloat(row)) : undefined;
    }
    if (aggregation === MetricsDashboardGraphTypeAggregation.Sum) {
        return values.sum(row => parseFloat(row));
    }
    return undefined;
}

// eslint-disable-next-line max-len
export function compareValues(leftValue: unknown, rightValue: unknown): number {
    if (typeof leftValue === "string") {
        return leftValue.localeCompare(rightValue as string);
    }
    if (leftValue instanceof Date && rightValue instanceof Date) {
        return leftValue.getTime() - rightValue.getTime();
    }

    return (leftValue as number) - (rightValue as number);
}

/* eslint-disable max-len */
const FILTERS: Record<Comparison, ((value: string | undefined | null, filterValue: FieldTypedValueBase | undefined) => boolean)> = {
    [Comparison.Equal]: (value, filterValue) => value === (filterValue?.string ?? filterValue?.number),
    [Comparison.NotEqual]: (value, filterValue) => value !== (filterValue?.string ?? filterValue?.number),
    [Comparison.GreaterThan]: (value, filterValue) => value != null && value > (filterValue?.string ?? filterValue?.number ?? 0),
    [Comparison.GreaterThanEqual]: (value, filterValue) => value != null && value >= (filterValue?.string ?? filterValue?.number ?? 0),
    [Comparison.LessThan]: (value, filterValue) => value != null && value < (filterValue?.string ?? filterValue?.number ?? 0),
    [Comparison.LessThanEqual]: (value, filterValue) => value != null && value <= (filterValue?.string ?? filterValue?.number ?? 0),
    // [Comparison.BeginsWith]: (value, filterValue) => value != null && value.toString().startsWith(filterValue?.string as string),
    [Comparison.Exists]: value => !!value,
    [Comparison.DoesntExist]: value => !value,
    [Comparison.Contains]: (value, filterValue) => value != null && (filterValue?.options ?? filterValue?.string?.split(",") ?? []).includes(value),
    [Comparison.DoesntContain]: (value, filterValue) => value == null || !(filterValue?.options ?? filterValue?.string?.split(",") ?? []).includes(value),
    [Comparison.In]: (value, filterValue) => value != null && (filterValue?.options ?? filterValue?.string?.split(",") ?? []).includes(value),
    [Comparison.Regexp]: (value, filterValue) => ((typeof value === "string" && filterValue?.string) ? new RegExp(filterValue.string).test(value) : false),
};
/* eslint-enable max-len */

export function filter(
    data: MetricsDashboardDataRow[],
    filters: MetricsDashboardGraphFilter[] | undefined | null,
    columnMap: Record<string, number>,
) {
    if (!filters || filters.isEmpty) {
        return data;
    }
    return data.filter(row => filters.some(filterItem => {
        const index = columnMap[filterItem.fieldId];
        const value = row.values[index];
        const comparer = FILTERS[filterItem.comparer ?? Comparison.Equal];
        return comparer(value, filterItem.value as FieldTypedValueBase | undefined);
    }));
}

export function formatDate(date: Date, dimension: MetricsDashboardGraphDimensionFormat) {
    if (dimension === MetricsDashboardGraphDimensionFormat.Year) {
        return date.getFullYear().toString();
    } if (dimension === MetricsDashboardGraphDimensionFormat.YearMonth) {
        return [date.getFullYear(), date.getMonth() + 1].map(row => row.toString().padStart(2, "0")).join("-");
    } if (dimension === MetricsDashboardGraphDimensionFormat.YearMonthDay) {
        return toInputDate(date);
    } if (dimension === MetricsDashboardGraphDimensionFormat.YearMonthDayHour) {
        const hours = `${date.getHours().toString().padStart(2, "0")}:00`;
        return `${toInputDate(date)} ${hours}`;
    } if (dimension === MetricsDashboardGraphDimensionFormat.YearMonthDayHourMinute) {
        return `${toInputDate(date)} ${toInputHourMinute(date)}`;
    }
    if (dimension === MetricsDashboardGraphDimensionFormat.DayOfWeek) {
        return date.toLocaleDateString(undefined, { weekday: "long" });
    }
    if (dimension === MetricsDashboardGraphDimensionFormat.Hour) {
        return `${date.getHours().toString().padStart(2, "0")}:00`;
    }
    if (dimension === MetricsDashboardGraphDimensionFormat.HourMinute) {
        return toInputHourMinute(date);
    }
    if (dimension === MetricsDashboardGraphDimensionFormat.UtcHourMinute) {
        return toUTCInputHourMinute(date);
    }
    if (dimension === MetricsDashboardGraphDimensionFormat.UtcHour) {
        return `${date.getUTCHours().toString().padStart(2, "0")}:00`;
    }
    if (dimension === MetricsDashboardGraphDimensionFormat.UtcDayOfWeek) {
        return date.toLocaleDateString(undefined, { weekday: "long", timeZone: "UTC" });
    }

    return "Invalid Format";
}

export function getSortValue(date: Date, dimension: MetricsDashboardGraphDimensionFormat) {
    if (dimension === MetricsDashboardGraphDimensionFormat.Year) {
        return date.getFullYear();
    } if (dimension === MetricsDashboardGraphDimensionFormat.YearMonth) {
        return date.getFullYear() * 100 + (date.getMonth() + 1);
    } if (dimension === MetricsDashboardGraphDimensionFormat.YearMonthDay) {
        return date.getFullYear() * 10000 + ((date.getMonth() + 1) * 100) + date.getDate();
    } if (dimension === MetricsDashboardGraphDimensionFormat.YearMonthDayHour) {
        return date.getFullYear() * 1000000 + ((date.getMonth() + 1) * 10000) + (date.getDate() * 100) + date.getHours();
    } if (dimension === MetricsDashboardGraphDimensionFormat.YearMonthDayHourMinute) {
        return date.getFullYear() * 100000000
            + ((date.getMonth() + 1) * 1000000)
            + (date.getDate() * 10000)
            + (date.getHours() * 100)
            + date.getMinutes();
    }
    if (dimension === MetricsDashboardGraphDimensionFormat.DayOfWeek) {
        return date.getDay();
    }
    if (dimension === MetricsDashboardGraphDimensionFormat.Hour) {
        return date.getHours();
    }
    if (dimension === MetricsDashboardGraphDimensionFormat.HourMinute) {
        return date.getHours() * 100 + date.getMinutes();
    }

    if (dimension === MetricsDashboardGraphDimensionFormat.UtcHour) {
        return date.getUTCHours();
    }
    if (dimension === MetricsDashboardGraphDimensionFormat.UtcHourMinute) {
        return (date.getUTCHours() * 100) + date.getUTCMinutes();
    }
    if (dimension === MetricsDashboardGraphDimensionFormat.UtcDayOfWeek) {
        return date.getUTCDay();
    }

    return "Invalid Format";
}

export class DataBuilder {
    private args: Partial<GraphProps> = Object.create(null);

    public get build(): GraphProps {
        return this.args as GraphProps;
    }

    public setTestId(testId: string) {
        this.args.testId = testId;
        return this;
    }

    public addLabel(id: string, value: string) {
        this.args.labelMap ??= {};
        this.args.labelMap[id] = value;

        return this;
    }

    public addColumn(id: string, name: string, type: FormFieldType) {
        this.args.data ??= {} as MetricsDashboardData;
        this.args.data.columns ??= [];
        this.args.columnIndexMap ??= {};
        const index = this.args.data.columns.push({
            id,
            field: {
                id,
                name,
                type,
                isSystemField: true,
                visibility: FormFieldVisibility.Reusable,
            },
        });
        this.args.columnIndexMap[id] = index - 1;

        return this;
    }

    public addRow(id: string, values: string[]) {
        this.args.data ??= {} as MetricsDashboardData;
        this.args.data.rows ??= [];
        this.args.data.rows.push({
            id,
            values,
        });

        return this;
    }

    public graph(title: string, dataSource: MetricsDashboardGraphDataSource, type: MetricsDashboardGraphType) {
        this.args.graph = {
            title,
            dataSource,
            type,
            height: 12,
            width: 12,
            locationX: 0,
            locationY: 0,
            metrics: this.args.graph?.metrics ?? [],
            dimensions: this.args.graph?.dimensions ?? [],
        } as MetricsDashboardGraph;

        return this;
    }

    public addMetric(metric: MetricsDashboardGraphMetric) {
        this.args.graph ??= Object.create(null) as MetricsDashboardGraph;
        this.args.graph.metrics ??= [];
        this.args.graph.metrics.push(metric);
        return this;
    }

    public addDimension(dimension: MetricsDashboardGraphDimension) {
        this.args.graph ??= Object.create(null) as MetricsDashboardGraph;
        this.args.graph.dimensions ??= [];
        this.args.graph.dimensions.push(dimension);
        return this;
    }
}

export function isNumeric(property?: FormFieldType) {
    return property === FormFieldType.Number || property === FormFieldType.Decimal;
}

export function isDate(property?: FormFieldType) {
    return property === FormFieldType.Date || property === FormFieldType.DateTime;
}

export function isDimension(
    property?: MetricsDashboardGraphDimension | MetricsDashboardGraphMetric,
): property is MetricsDashboardGraphDimension {
    return property?.__typename === "MetricsDashboardGraphDimension"
        || (property as MetricsDashboardGraphDimension)?.format != null;
}

export function isMetric(
    property?: MetricsDashboardGraphDimension | MetricsDashboardGraphMetric,
): property is MetricsDashboardGraphMetric {
    return property?.__typename === "MetricsDashboardGraphMetric"
        || (property as MetricsDashboardGraphMetric)?.display != null;
}
